import { useEffect, useMemo, useState } from 'react';
import {
  WHEEL_CX,
  WHEEL_CY,
  WHEEL_HUB_RADIUS,
  WHEEL_ON_WHEEL_LABEL_LIMIT,
  WHEEL_RADIUS,
  WHEEL_TEXT_INNER_R,
  WHEEL_TEXT_OUTER_R,
  WHEEL_VIEWBOX_SIZE,
  buildWheelSegments,
  wheelMobileShowsRadialText,
  wheelRadialLabelPose,
  type WheelSegment,
  type WheelSegmentInput,
} from '@shared';

const DESKTOP_LAYOUT_MQ = '(min-width: 768px)';

function polar(
  cx: number,
  cy: number,
  r: number,
  angleFromTopCw: number,
): { x: number; y: number } {
  const rad = ((-90 + angleFromTopCw) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function segmentPath(segment: WheelSegment): string {
  const sweep = segment.endDeg - segment.startDeg;
  if (sweep >= 359.999) {
    return '';
  }
  const start = polar(WHEEL_CX, WHEEL_CY, WHEEL_RADIUS, segment.startDeg);
  const end = polar(WHEEL_CX, WHEEL_CY, WHEEL_RADIUS, segment.endDeg);
  const largeArc = sweep > 180 ? 1 : 0;
  return [
    `M ${WHEEL_CX} ${WHEEL_CY}`,
    `L ${start.x} ${start.y}`,
    `A ${WHEEL_RADIUS} ${WHEEL_RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
}

function useDesktopLayout(override?: 'mobile' | 'desktop'): boolean {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (override) return override === 'desktop';
    if (typeof window === 'undefined') return false;
    return window.matchMedia(DESKTOP_LAYOUT_MQ).matches;
  });

  useEffect(() => {
    if (override) {
      setIsDesktop(override === 'desktop');
      return;
    }
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(DESKTOP_LAYOUT_MQ);
    const sync = (): void => {
      setIsDesktop(media.matches);
    };
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [override]);

  return isDesktop;
}

function SegmentRadialLabel({
  segment,
  lines,
  visibleLabel,
  className,
}: {
  segment: WheelSegment;
  lines: readonly string[];
  visibleLabel: string;
  className: string;
}): JSX.Element {
  const pose = wheelRadialLabelPose(segment.midDeg);
  const lineHeight = segment.labelMode === 'full' ? 13 : 11;
  const safeLines = lines.length > 0 ? lines : [visibleLabel];
  return (
    <text
      x={pose.x}
      y={pose.y}
      textAnchor="middle"
      dominantBaseline="middle"
      className={className}
      transform={`rotate(${pose.rotateDeg}, ${pose.x}, ${pose.y})`}
      data-radial="true"
      data-flipped={pose.flipped ? 'true' : 'false'}
      data-mid-deg={String(segment.midDeg)}
      data-visible-label={visibleLabel}
      aria-hidden="true"
    >
      {safeLines.length === 1 ? (
        safeLines[0]
      ) : (
        <>
          <tspan x={pose.x} dy={-lineHeight / 2}>
            {safeLines[0]}
          </tspan>
          <tspan x={pose.x} dy={lineHeight}>
            {safeLines[1]}
          </tspan>
        </>
      )}
    </text>
  );
}

export type StrainWheelStrain = WheelSegmentInput;

export type StrainWheelProps = {
  strains: readonly StrainWheelStrain[];
  rotationDeg: number;
  durationMs: number;
  spinning: boolean;
  selectedId: string | null;
  onSpinSettled?: () => void;
  /** Optional center spin control (stays fixed while the rotor turns). */
  onSpin?: () => void;
  spinDisabled?: boolean;
  spinLabel?: string;
  /** Force layout model (tests); otherwise uses min-width: 768px. */
  layout?: 'mobile' | 'desktop';
};

/**
 * Visual strain wheel. Parent chooses the winner before animating `rotationDeg`.
 * Segments are text-only (catalogue Product Name); no product images on slices.
 */
export function StrainWheel({
  strains,
  rotationDeg,
  durationMs,
  spinning,
  selectedId,
  onSpinSettled,
  onSpin,
  spinDisabled = false,
  spinLabel = 'Spin',
  layout,
}: StrainWheelProps): JSX.Element {
  const isDesktop = useDesktopLayout(layout);
  const segments = useMemo(() => buildWheelSegments(strains), [strains]);

  const labelMode = segments[0]?.labelMode ?? 'full';
  const showNameText =
    (isDesktop && segments.some((s) => s.showLabel)) ||
    (!isDesktop && wheelMobileShowsRadialText(labelMode));
  const showDesktopLegend =
    isDesktop && (labelMode === 'initials' || labelMode === 'number');
  const largeCatalogue = strains.length > WHEEL_ON_WHEEL_LABEL_LIMIT;
  const selectedSegment =
    selectedId == null
      ? null
      : (segments.find((s) => s.id === selectedId) ?? null);

  return (
    <div
      className={`strain-wheel strain-wheel--${isDesktop ? 'desktop' : 'mobile'} strain-wheel--text-only`}
      data-layout={isDesktop ? 'desktop' : 'mobile'}
      data-label-mode={labelMode}
      data-text-outer={String(WHEEL_TEXT_OUTER_R)}
      data-text-inner={String(WHEEL_TEXT_INNER_R)}
    >
      <div className="strain-wheel__stage">
        <div className="strain-wheel__pointer" aria-hidden="true" />
        <div
          className={`strain-wheel__rotor${spinning ? ' strain-wheel__rotor--spinning' : ''}`}
          style={{
            transform: `rotate(${rotationDeg}deg)`,
            transition:
              spinning && durationMs > 0
                ? `transform ${durationMs}ms cubic-bezier(0.12, 0.75, 0.12, 1)`
                : 'none',
          }}
          onTransitionEnd={(event) => {
            if (event.propertyName !== 'transform') return;
            if (event.target !== event.currentTarget) return;
            onSpinSettled?.();
          }}
        >
          <svg
            className="strain-wheel__svg"
            viewBox={`0 0 ${WHEEL_VIEWBOX_SIZE} ${WHEEL_VIEWBOX_SIZE}`}
            role="img"
            aria-label={
              strains.length === 0
                ? 'Empty strain wheel'
                : `Strain wheel with ${strains.length} eligible strains`
            }
          >
            {segments.length === 1 ? (
              <g>
                <title>{segments[0]!.accessibleLabel}</title>
                <circle
                  cx={WHEEL_CX}
                  cy={WHEEL_CY}
                  r={WHEEL_RADIUS}
                  fill={segments[0]!.color}
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth={2}
                  className={
                    selectedId === segments[0]!.id && !spinning
                      ? 'strain-wheel__segment strain-wheel__segment--selected'
                      : 'strain-wheel__segment'
                  }
                />
              </g>
            ) : (
              segments.map((segment) => (
                <g key={segment.id}>
                  <title>{segment.accessibleLabel}</title>
                  <path
                    d={segmentPath(segment)}
                    fill={segment.color}
                    stroke="rgba(255,255,255,0.55)"
                    strokeWidth={2}
                    className={
                      selectedId === segment.id && !spinning
                        ? 'strain-wheel__segment strain-wheel__segment--selected'
                        : 'strain-wheel__segment'
                    }
                  />
                </g>
              ))
            )}
            {segments.map((segment) => {
              const showNumber = labelMode === 'number';
              const showInitials = labelMode === 'initials';
              const showText =
                showNameText || showNumber || showInitials || !isDesktop;
              if (!showText) return null;

              const lines = showNumber
                ? [String(segment.legendNumber)]
                : showNameText
                  ? segment.displayLines
                  : [segment.initials];
              const visibleLabel = lines.join(' ');
              const textClass = showNameText
                ? `strain-wheel__label strain-wheel__label--radial strain-wheel__label--${labelMode}`
                : 'strain-wheel__label strain-wheel__label--radial strain-wheel__label--marker';

              return (
                <SegmentRadialLabel
                  key={`${segment.id}-label`}
                  segment={segment}
                  lines={lines}
                  visibleLabel={visibleLabel}
                  className={textClass}
                />
              );
            })}
            <circle
              cx={WHEEL_CX}
              cy={WHEEL_CY}
              r={WHEEL_TEXT_INNER_R}
              fill="none"
              className="strain-wheel__text-inner-guide"
              aria-hidden="true"
            />
            <circle
              cx={WHEEL_CX}
              cy={WHEEL_CY}
              r={WHEEL_HUB_RADIUS}
              className="strain-wheel__hub"
            />
          </svg>
        </div>
        {onSpin && (
          <button
            type="button"
            className="strain-wheel__spin"
            disabled={spinDisabled || spinning}
            onClick={onSpin}
          >
            {spinLabel}
          </button>
        )}
      </div>
      {selectedSegment && !spinning && (
        <p className="strain-wheel__focus-name" aria-live="polite">
          {selectedSegment.label}
        </p>
      )}
      {showDesktopLegend && (
        <ol className="strain-wheel__legend-list" aria-label="Wheel strains">
          {segments.map((segment) => {
            const selected = selectedId === segment.id && !spinning;
            return (
              <li
                key={`legend-${segment.id}`}
                className={
                  selected
                    ? 'strain-wheel__legend-item strain-wheel__legend-item--selected'
                    : 'strain-wheel__legend-item'
                }
              >
                <span
                  className="strain-wheel__legend-swatch"
                  style={{ background: segment.color }}
                  aria-hidden="true"
                >
                  {segment.legendNumber}
                </span>
                <span>{segment.label}</span>
              </li>
            );
          })}
        </ol>
      )}
      {!isDesktop && largeCatalogue && (
        <p className="strain-wheel__legend hint muted">
          {strains.length} products on the wheel — concise labels and the result
          card identify each pick. Full Product Names appear after the wheel
          stops.
        </p>
      )}
      {isDesktop && !showDesktopLegend && largeCatalogue && (
        <p className="strain-wheel__legend hint muted">
          {strains.length} products on the wheel — labels stay concise so wedges
          stay readable. The selected product is confirmed after the wheel
          stops.
        </p>
      )}
      {segments.length === 1 && (
        <p className="strain-wheel__legend hint muted">
          Only one eligible strain is in the wheel.
        </p>
      )}
    </div>
  );
}
